import LoggerApiTokenManager from '../lib/LoggerApiTokenManager';
import { networkErrorApiWrapper } from '../lib/NetworkErrorHandler';
import { urnApiWrapper } from '../lib/urnApiWrapper';
import { config } from '../../config';
// @ts-nocheck
export type ThenArg<T> =
  T extends Promise<infer U>
    ? U
    : T extends (...args: any[]) => Promise<infer U>
      ? U
      : T;

type QueryParameters = {
  [key: string]:
    | string
    | number
    | string[]
    | number[]
    | boolean
    | boolean[]
    | undefined;
};

export type Error = {
  statusCode: number;
  name: string;
  userinfo?: string;
};

class ApiError {
  public message: string;
  public details: Error | null = null;

  constructor(message: string) {
    this.message = message;
  }
}

/**
 * API for managing logs

 * @class LoggerApi
 * @param {(string)} [domainOrOptions] - The project domain.
 */
class LoggerApi {
  protected baseUrl: string = "";
  protected headers: { name: string; value: string }[] = [];
  protected token: string = "";

  serializeQueryParams(parameters: QueryParameters) {
    return Object.keys(parameters)
      .reduce((acc: string[], p) => {
        const param = parameters[p];
        if (typeof param === "undefined" || param === "") {
          return acc;
        }

        if (Array.isArray(parameters[p])) {
          return [
            ...acc,
            ...parameters[p].map(
              (v) => `${encodeURIComponent(p)}=${encodeURIComponent(v)}`,
            ),
          ];
        } else {
          return [
            ...acc,
            `${encodeURIComponent(p)}=${encodeURIComponent(
              String(parameters[p]),
            )}`,
          ];
        }
      }, [])
      .join("&");
  }

  protected transformParameter(value: any, transformOperation?: string) {
    switch (transformOperation) {
      case "joinUsingPipes":
        return Array.isArray(value) ? value.join("|") : value;
      default:
        return value;
    }
  }

  public setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  public setHeaders(headers: { name: string; value: string }[]) {
    this.headers = headers;
  }

  public setToken(token: string) {
    this.token = token;
  }

  protected appendAuthHeaders(headerParams: Headers) {
    const headers = new Headers(headerParams);
    if (this.token) {
      headers.append("Authorization", `Bearer ${this.token}`);
    }
    return headers;
  }

  private async request(
    method: string,
    url: string,
    body: any,
    headers: Headers = new Headers(),
    queryParameters: QueryParameters = {},
  ) {
    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? this.serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? "?" + queryParams : "");

    if (headers.get("Content-Type") === "multipart/form-data") {
      const form = new FormData();
      for (let k in body) {
        const value = body[k];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            form.append(k, v);
          });
        } else {
          form.append(k, value);
        }
      }
      body = form;
    } else if (
      headers.get("Content-Type") === "application/json" &&
      body &&
      Object.keys(body).length > 0
    ) {
      body = JSON.stringify(body);
    } else {
      body = undefined;
    }

    if (headers.get("Content-Type") === "multipart/form-data") {
      headers.delete("Content-Type");
    }

    if (this.headers.length > 0) {
      this.headers.forEach((h) => {
        headers.append(h.name, h.value);
      });
    }

    const response = await fetch(urlWithParams, { method, headers, body });

    if (response.status === 204) return response;

    if (response.ok) {
      const responseContentType =
        (response.headers && response.headers.get("Content-Type")) || "";
      if (responseContentType.includes("application/json")) {
        return response.json();
      }
      return response;
    } else {
      const err = new ApiError(response.statusText);
      err.details = await response.json();
      throw err;
    }
  }

  /**
   * Save log to database
   * @method
   * @name LoggerApi#saveLog
   */
  saveLog(
    parameters: {} = {},
    body: {
      identifier?: string;
      severity:
        | "DEBUG"
        | "debug"
        | "INFO"
        | "info"
        | "NOTICE"
        | "notice"
        | "WARNING"
        | "warning"
        | "ERROR"
        | "error"
        | "CRITICAL"
        | "critical"
        | "ALERT"
        | "alert"
        | "EMERGENCY"
        | "emergency";
      dataType:
        | "JSON"
        | "json"
        | "CEF"
        | "cef"
        | "SYSLOG"
        | "syslog"
        | "LEEF"
        | "leef"
        | "XML"
        | "xml"
        | "PLAIN_TEXT"
        | "plain_text";
      clientAccId: string;
      data: object | string;
    },
  ): Promise<void> {
    let path = "/logs";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
}

const client = new LoggerApi();
client.setBaseUrl(config.VITE_LOGGER_API_URL);
export default LoggerApiTokenManager.authApiProxy(networkErrorApiWrapper(urnApiWrapper(client, 'LoggerApi')));
export { LoggerApi };
